import React from 'react';

import { KatSpinner } from '@amzn/katal-react';
import { useBundle } from '@amzn/react-arb-tools';
import { MessageBundleId } from 'src/constants/LocalizationConstants';

interface TranslationBundle {
  getMessage: (id: string) => string;
  formatMessage: (id: string, ...args: any) => string;
}
export interface ITranslationContext {
  commonBundle: TranslationBundle;
  menuBundle: TranslationBundle;
  fileUploaderBundle: TranslationBundle;
  messageBundle: TranslationBundle;
  gtsBundle: TranslationBundle;
  productSafetyBundle: TranslationBundle;
  socialResponsibilityBundle: TranslationBundle;
  socialResponsibilityLandingPageBundle: TranslationBundle;
  dsaOutOfCourtSettlementBundle: TranslationBundle;
  caseDetailsBundle: TranslationBundle;
  caseListBundle: TranslationBundle;
  caseSummaryBundle: TranslationBundle;
  communicationPageBundle: TranslationBundle;
  createCaseBundle: TranslationBundle;
  csamBundle: TranslationBundle;
  paymentServicesComplaintBundle: TranslationBundle;
}

export const TranslationContext = React.createContext<ITranslationContext>(
  {} as any,
);

const TranslationProvider = ({ children }) => {
  const [commonBundle, isCommonBundleLoading] = useBundle(
    MessageBundleId.COMMON_BUNDLE,
  );
  const [menuBundle, isMenuBundleLoading] = useBundle(
    MessageBundleId.MENU_BUNDLE,
  );
  const [fileUploaderBundle, isFileUploaderBundleLoading] = useBundle(
    MessageBundleId.FILE_UPLOADER_BUNDLE,
  );
  const [messageBundle, isMessageBundleLoading] = useBundle(
    MessageBundleId.MESSAGE_BUNDLE,
  );
  const [gtsBundle, isGtsBundleLoading] = useBundle(MessageBundleId.GTS_BUNDLE);
  const [productSafetyBundle, isProductSafetyBundleLoading] = useBundle(
    MessageBundleId.PRODUCT_SAFETY_BUNDLE,
  );
  const [socialResponsibilityBundle, isSocialResponsibilityBundleLoading] =
    useBundle(MessageBundleId.SOCIAL_RESPONSIBILITY_BUNDLE);
  const [
    dsaOutOfCourtSettlementBundle,
    isDsaOutOfCourtSettlementBundleLoading,
  ] = useBundle(MessageBundleId.DSA_OUT_OF_COURT_SETTLEMENT_BUNDLE);
  const [csamBundle, isCSAMBundleLoading] = useBundle(
    MessageBundleId.CSAM_BUNDLE,
  );
  const [
    socialResponsibilityLandingPageBundle,
    isSocialResponsibilityLandingPageBundleLoading,
  ] = useBundle(MessageBundleId.SOCIAL_RESPONSIBILITY_LANDING_PAGE_BUNDLE);
  const [caseDetailsBundle, isCaseDetailsBundleLoading] = useBundle(
    MessageBundleId.CASE_DETAILS_BUNDLE,
  );
  const [caseListBundle, isCaseListBundleLoading] = useBundle(
    MessageBundleId.CASE_LIST_BUNDLE,
  );
  const [caseSummaryBundle, isCaseSummaryBundleLoading] = useBundle(
    MessageBundleId.CASE_SUMMARY_BUNDLE,
  );
  const [communicationPageBundle, isCommunicationPageBundleLoading] = useBundle(
    MessageBundleId.COMMUNICATION_PAGE_BUNDLE,
  );
  const [createCaseBundle, isCreateCaseBundleLoading] = useBundle(
    MessageBundleId.CREATE_CASE_BUNDLE,
  );
  const [
    paymentServicesComplaintBundle,
    isPaymentServicesComplaintBundleLoading,
  ] = useBundle(MessageBundleId.PAYEMENT_SERVICES_COMPLAINT_BUNDLE);

  if (
    isCommonBundleLoading ||
    isMenuBundleLoading ||
    isFileUploaderBundleLoading ||
    isMessageBundleLoading ||
    isGtsBundleLoading ||
    isProductSafetyBundleLoading ||
    isSocialResponsibilityLandingPageBundleLoading ||
    isSocialResponsibilityBundleLoading ||
    isDsaOutOfCourtSettlementBundleLoading ||
    isCSAMBundleLoading ||
    isCaseDetailsBundleLoading ||
    isCommunicationPageBundleLoading ||
    isCaseListBundleLoading ||
    isCreateCaseBundleLoading ||
    isCaseSummaryBundleLoading ||
    isPaymentServicesComplaintBundleLoading
  ) {
    return <KatSpinner size="large" variant="default"></KatSpinner>;
  }

  const bundles = {
    commonBundle,
    menuBundle,
    fileUploaderBundle,
    messageBundle,
    gtsBundle,
    productSafetyBundle,
    socialResponsibilityBundle,
    socialResponsibilityLandingPageBundle,
    dsaOutOfCourtSettlementBundle,
    csamBundle,
    caseDetailsBundle,
    caseListBundle,
    caseSummaryBundle,
    communicationPageBundle,
    createCaseBundle,
    paymentServicesComplaintBundle,
  };

  return (
    <TranslationContext.Provider value={bundles}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationProvider;
